import { Close, Done } from '@mui/icons-material'
import {
  Box,
  Divider,
  MobileStepper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import React, { FunctionComponent } from 'react'
import Step1 from './step1'
import Step2 from './step2'
import { CreateInvoiceReq } from '@/services/mobi-api/invoice/types'
import { useMobiApi } from '@/services/mobi-api'
import { useRouter } from 'next/navigation'
import { useToast } from '@/context/ToastContext'
import useIntl from '@/utils/intl'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { MNP75Unit } from '@/services/mobi-api/mnp75/types'
import Image from 'next/image'
import classNames from 'classnames'
import { resolveImageUrl } from '@/utils/constants'

interface Props {
  onClose: () => void
}

const WifiCard: FunctionComponent<Props> = ({ onClose }) => {
  const router = useRouter()
  const { showError } = useToast()
  const { translate } = useIntl()
  const { invoice: invoiceApi } = useMobiApi()

  const [currentStep, setCurrentStep] = React.useState(0)
  const [invoice, setInvoice] = React.useState<CreateInvoiceReq | undefined>()
  const [option, setOption] = React.useState<MNP75Unit | undefined>()

  const steps = [
    {
      label: translate('phoneNumber.insert'),
      description: translate('chooseCard')
    },
    {
      label: translate('shop.vat'),
      description: translate('vat.options')
    },
    {
      label: translate('payment'),
      description: translate('apporcard')
    }
  ]

  const handleNext = (payload: CreateInvoiceReq, option?: MNP75Unit): void => {
    const mergedInvoice = {
      ...invoice,
      ...payload
    }
    setInvoice(mergedInvoice)
    if (option !== undefined) {
      setOption(option)
    }
    if (currentStep === 0) {
      setCurrentStep(currentStep + 1)
    } else {
      void createInvoice(mergedInvoice)
    }
  }
  const handleClickStep = (idx: number): void => {
    if (idx < currentStep) {
      setCurrentStep(idx)
    }
  }
  const createInvoice = async (payload: CreateInvoiceReq): Promise<void> => {
    try {
      const res = await invoiceApi.create(payload)
      if (res?.redirectUrl !== undefined && res.redirectUrl !== '') {
        router.push(res.redirectUrl)
      } else {
        showError(translate('error'))
      }
    } catch (e) {
      console.error('CREATE INVOICE FAILED: ', e)
    }
  }
  const stepIcon = (index: number): any => {
    return index === currentStep
      ? (
        <div className='bg-secondary p-1 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
          <span className='text-textInverse font-extrabold text-xs md:text-sm'>
            {index + 1}
          </span>
        </div>
        )
      : index <= (currentStep ?? 0)
        ? (
          <div className='bg-[#F0FDF4] border border-[#16A34A] p-2 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <Done className='text-[#16A34A] text-xs md:text-sm font-bold' />
          </div>
          )
        : (
          <div className='bg-[#231F1F1A] p-1 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <span className='text-[#B0B0B0] font-extrabold text-xs md:text-sm'>
              {index + 1}
            </span>
          </div>
          )
  }
  return (
    <div className='flex md:flex-row flex-col h-[90vh]'>
      <div className='bg-surfaceInverse flex flex-col xl:w-1/4 lg:w-2/5'>
        <div className='lg:p-14 md:p-8 p-4'>
          <div className='md:flex flex-col items-start hidden'>
            <Image
              draggable={false}
              width={50}
              height={50}
              src={resolveImageUrl('/uploads/mobicom-logo-hq.png')}
              alt='logo'
              className='w-16'
            />
            <div>
              <h1 className='lg:text-2xl md:text-xl font-bold text-textHigh'>
                {translate('quick.buyWifiCard')}
              </h1>
              <span className='text-textMid text-xs 2xl:text-sm font-medium'>
                {translate('simple')}
              </span>
            </div>
          </div>
          <div className='md:hidden flex justify-between items-center'>
            <div className='flex items-center'>
              <h1 className='font-bold text-sm text-textHigh border-r pr-4 mr-4'>
                {translate('quick.buyWifiCard')}
              </h1>
              <MobileStepper
                className='bg-surfaceInverse'
                color='error'
                variant='dots'
                sx={{
                  '.MuiMobileStepper-dotActive': {
                    backgroundColor: 'red'
                  }
                }}
                steps={steps.length}
                position='static'
                activeStep={currentStep}
                backButton={<></>}
                nextButton={<></>}
              />
              <div
                className={classNames(
                  '',
                  currentStep === 0 ? 'invisible' : 'visible'
                )}
              >
                <DarkButton
                  type='soft'
                  fullwidth
                  size='sm'
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  {' '}
                  {translate('back')}
                </DarkButton>
              </div>
            </div>
            <DarkIconButton
              type='soft'
              size='sm'
              icon={<Close />}
              onClick={onClose}
            />
          </div>
        </div>
        <Divider className='hidden md:flex' />
        <div className='lg:p-14 md:p-8'>
          <div className='hidden md:flex'>
            <Stepper orientation='vertical' activeStep={currentStep}>
              {steps.map((step, index) => (
                <Step
                  key={index}
                  onClick={() => {
                    handleClickStep(index)
                  }}
                >
                  <StepLabel StepIconComponent={() => stepIcon(index)}>
                    <Typography className='text-sm font-bold text-textHigh'>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography className='text-xs text-textMid font-medium'>
                      {step.description}
                    </Typography>
                    {index > 0
                      ? (
                        <Box sx={{ mt: 2 }}>
                          <DarkButton
                            type='soft'
                            size='sm'
                            onClick={() => setCurrentStep(index - 1)}
                          >
                            {translate('back')}
                          </DarkButton>
                        </Box>
                        )
                      : (
                        <></>
                        )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </div>
        </div>
      </div>
      <Divider orientation='vertical' flexItem />
      <div className='bg-surfaceWebGrey lg:p-6 md:p-3 w-full h-full'>
        <div className='flex justify-end w-full'>
          <div className='hidden md:flex'>
            <DarkIconButton
              type='soft'
              size='sm'
              icon={<Close />}
              onClick={onClose}
            />
          </div>
        </div>
        <div className='h-full flex flex-col justify-center items-center md:-mt-3'>
          {currentStep === 0
            ? (
              <Step1 onClick={handleNext} />
              )
            : currentStep === 1
              ? (
                <Step2 onClick={handleNext} invoice={invoice} option={option} />
                )
              : (
                <div />
                )}
        </div>
      </div>
    </div>
  )
}
export default WifiCard
